// @ts-nocheck
// @ts-ignore
/**
 * TabsManager — tabbed product carousels (homepage / L1) with INP-friendly loading.
 *
 * Flow (high level):
 *  1. Liquid renders skeletons only (no heavy product cards up front).
 *  2. When ~10% of the section is visible → GraphQL fetch (active tab first).
 *  3. Other tabs prefetch in the background (~2s later).
 *  4. Each tab gets its own Swiper panel (init once); tab switch = show/hide panel
 *     via `.is-active` — no destroy/re-init of a shared Swiper.
 *  5. Tab click: update active button sync (fast paint); heavy work in setTimeout(0).
 *
 * Cache: store GraphQL-built HTML shells only — never re-save hydrated custom-element DOM
 * (that used to break swatches/wishlist when revisiting a tab).
 *
 * Card types via `data-product-card`: "1" | "2" | "3"
 * Tab CTA: if buttons have `data-tab-cta-text` / `data-tab-cta-link`, CTA updates on switch.
 * Skeleton widths: `data-slides-mobile` / `data-slides-tablet` / `data-slides-desktop`
 *
 * Usage:
 *   <tabs-manager data-product-card="3" data-slides-mobile="1.1" data-slides-tablet="2" data-slides-desktop="2">
 */
class TabsManager extends HTMLElement {
    constructor() {
        super();
        this.observer = null;
        this.isFetched = false;
        this.SHOPIFY_STOREFRONT_ACCESS_TOKEN = window.storefrontAccessToken;
        this.SHOPIFY_DOMAIN = window.storeDomain;
        this.SHOPIFY_API_VERSION = '2026-01';
        this.htmlCache = {}; // GraphQL product HTML per collection (shell only)
        this._inflightFetches = new Map(); // dedupe in-flight GraphQL by collectionId
        this._tabCtaMode = false;
        this._tabDrivenCtaBtn = null;
        this._tabDrivenCtaLabel = null;
        this._tabDrivenCtaIcon = null;
        this._activeCollectionId = null; // source of truth while async work runs
        /** One panel + one Swiper instance per tab/collection (created once). */
        /** @type {Map<string, { panel: HTMLElement, swiperEl: HTMLElement, wrapper: HTMLElement, paginationEl: HTMLElement|null, swiper: object|null, contentReady: boolean }>} */
        this._panels = new Map();
        this._swiperOptions = null;
        this._panelsReady = false;
        this._sharedNavBound = false;
        // Read from self; will be resolved fully in connectedCallback
        this.productCard = (this.getAttribute('data-product-card') || '').trim();
    }

    connectedCallback() {
        // If not set on <tabs-manager>, fall back to reading from inner <swiper-component>,
        // then default to card type '3' (neo-product-card-3)
        if (!this.productCard) {
            const swiperComp = this.querySelector('swiper-component');
            this.productCard = (swiperComp?.getAttribute('data-product-card') || '3').trim();
        }

        this.initTabClicks();
        this.initTabCtaRefs();
        this._ensureTabsManagerStyles();

        // Skeleton widths: section owns slidesPerView values via data-* attrs.
        const slidesMobile = this.getAttribute('data-slides-mobile') || '1.2';
        const slidesTablet = this.getAttribute('data-slides-tablet') || slidesMobile;
        const slidesDesktop = this.getAttribute('data-slides-desktop') || slidesTablet;
        this.style.setProperty('--slides-mobile', slidesMobile);
        this.style.setProperty('--slides-tablet', slidesTablet);
        this.style.setProperty('--slides-desktop', slidesDesktop);

        this._activeCollectionId =
            this.querySelector('button[data-collection-id].active')?.getAttribute('data-collection-id') ||
            this.querySelector('[data-collection-id].active')?.getAttribute('data-collection-id') ||
            null;

        // Defer panel setup so Liquid's swiper-component can connect first, then we replace it.
        setTimeout(() => {
            this._ensurePanelsStructure();
            this._initSkeletonLayer();
            this.initObserver();
            this._updateTabCta(
                this.querySelector(`button[data-collection-id="${this._activeCollectionId}"]`) ||
                    this.querySelector('[data-collection-id].active')
            );
        }, 0);
    }

    _isMobileViewport() {
        return (window.innerWidthCached ?? window.innerWidth) < 650;
    }

    /**
     * Injects shared CSS once:
     * - inactive panels use display:none (cheaper than opacity for paint/INP)
     * - active panel uses .is-active
     * - skeleton overlay uses .is-visible
     * - skeleton slide widths from CSS vars (--slides-mobile/tablet/desktop)
     */
    _ensureTabsManagerStyles() {
        if (TabsManager._stylesReady) return;
        TabsManager._stylesReady = true;

        const style = document.createElement('style');
        style.textContent = `
            tabs-manager .swiper { position: relative; }
            tabs-manager button[data-collection-id].active {
                pointer-events: none;
            }
            tabs-manager .tabs-manager-panels {
                position: relative;
                width: 100%;
            }
            tabs-manager .tabs-manager-panel {
                display: none;
                width: 100%;
            }
            tabs-manager .tabs-manager-panel.is-active {
                display: block;
            }
            tabs-manager .tabs-manager-hidden {
                display: none !important;
            }
            tabs-manager .tabs-manager-skeleton-layer {
                position: absolute;
                inset: 0;
                z-index: 2;
                display: flex;
                align-items: center;
                gap: 10px;
                overflow: hidden;
                pointer-events: none;
                visibility: hidden;
            }
            tabs-manager .tabs-manager-skeleton-layer.is-visible {
                visibility: visible;
            }
            tabs-manager .tabs-manager-skeleton-layer .swiper-slide {
                width: calc(100% / var(--slides-mobile, 1.2));
                flex-shrink: 0;
                box-sizing: border-box;
                opacity: 1 !important;
            }
            @media (min-width: 600px) {
                tabs-manager .tabs-manager-skeleton-layer .swiper-slide {
                    width: calc(100% / var(--slides-tablet, 2));
                }
            }
            @media (min-width: 1100px) {
                tabs-manager .tabs-manager-skeleton-layer .swiper-slide {
                    width: calc(100% / var(--slides-desktop, 4));
                }
            }
        `;
        document.head.appendChild(style);
    }

    _parseSwiperOptions() {
        if (this._swiperOptions) return this._swiperOptions;

        const swiperComp = this.querySelector('swiper-component');
        const optionsEl = swiperComp?.querySelector('swiper-options');
        let options = {};

        if (optionsEl) {
            const optionsContent = optionsEl.textContent.trim();
            const formattedContent = optionsContent.startsWith('{') ? optionsContent : `{${optionsContent}}`;
            try {
                const parser = new Function('component', 'element', `return ${formattedContent}`);
                options = parser.call(swiperComp, swiperComp, swiperComp.querySelector('.swiper')) || {};
            } catch (err) {
                console.error('TabsManager: Error parsing swiper-options.', err);
            }
            optionsEl.classList.add('tabs-manager-hidden');
        }

        if (options.breakpoints) {
            const windowWidth = window.innerWidthCached ?? window.innerWidth;
            const breakpointKeys = Object.keys(options.breakpoints).map(Number).sort((a, b) => a - b);
            let activeBreakpointSettings = {};
            for (const breakpoint of breakpointKeys) {
                if (windowWidth >= breakpoint) {
                    activeBreakpointSettings = options.breakpoints[breakpoint];
                } else {
                    break;
                }
            }
            options = { ...options, ...activeBreakpointSettings };
        }

        this._swiperOptions = options;
        return options;
    }

    _buildOptionsForPanel(panel) {
        const base = { ...(this._parseSwiperOptions() || {}) };
        // Shared nav is handled by TabsManager so multiple panels don't fight over the same buttons.
        delete base.navigation;

        if (base.pagination && panel.paginationEl) {
            base.pagination = {
                ...base.pagination,
                el: panel.paginationEl,
            };
        } else {
            delete base.pagination;
        }

        return base;
    }

    /**
     * Build one panel per tab inside the section.
     * Replaces the single Liquid Swiper so we never destroy/re-init on tab switch (INP).
     */
    _ensurePanelsStructure() {
        if (this._panelsReady) return;

        const swiperComp = this.querySelector('swiper-component');
        if (!swiperComp) return;

        const originalSwiper = swiperComp.querySelector('.swiper');
        if (!originalSwiper) return;

        // Tear down auto-inited single Swiper — we own instances per panel now.
        // @ts-ignore
        const existing = typeof swiperComp.getSwiper === 'function' ? swiperComp.getSwiper() : swiperComp.swiper;
        if (existing && typeof existing.destroy === 'function') {
            existing.destroy(true, true);
        }
        // @ts-ignore
        swiperComp.swiper = null;
        // Prevent swiper-component from creating another root instance.
        // @ts-ignore
        swiperComp.init = function () { return; };

        const tabs = this._getUniqueCollectionTabs();
        if (!tabs.length) return;

        const activeId =
            this._activeCollectionId || tabs[0].getAttribute('data-collection-id');

        const paginationOriginal = originalSwiper.querySelector('.swiper-pagination');
        const swiperClassName = originalSwiper.className;

        const panelsRoot = document.createElement('div');
        panelsRoot.className = 'tabs-manager-panels';

        tabs.forEach((tab) => {
            const collectionId = tab.getAttribute('data-collection-id');
            if (!collectionId) return;

            const panel = document.createElement('div');
            panel.className = 'tabs-manager-panel';
            panel.setAttribute('data-panel-collection-id', collectionId);
            if (collectionId === activeId) panel.classList.add('is-active');

            const swiperEl = document.createElement('div');
            swiperEl.className = swiperClassName;

            const wrapper = document.createElement('div');
            wrapper.className = 'swiper-wrapper fp-main-wrapper';
            wrapper.innerHTML = this.renderSkeletons();

            swiperEl.appendChild(wrapper);

            let paginationEl = null;
            if (paginationOriginal) {
                paginationEl = paginationOriginal.cloneNode(false);
                paginationEl.className = paginationOriginal.className;
                swiperEl.appendChild(paginationEl);
            }

            panel.appendChild(swiperEl);
            panelsRoot.appendChild(panel);

            this._panels.set(collectionId, {
                panel,
                swiperEl,
                wrapper,
                paginationEl,
                swiper: null,
                contentReady: false,
            });
        });

        originalSwiper.replaceWith(panelsRoot);
        this._panelsRoot = panelsRoot;
        this._panelsReady = true;

        this._bindSharedNavigation();
    }

    _bindSharedNavigation() {
        if (this._sharedNavBound) return;
        const options = this._parseSwiperOptions();
        const nav = options?.navigation;
        if (!nav) return;

        const resolveEl = (sel) => {
            if (!sel) return null;
            if (typeof sel !== 'string') return sel;
            return this.querySelector(sel) || document.querySelector(sel);
        };

        const nextEl = resolveEl(nav.nextEl);
        const prevEl = resolveEl(nav.prevEl);
        if (!nextEl && !prevEl) return;

        const onNext = (e) => {
            e.preventDefault();
            e.stopPropagation();
            this._getActivePanelSwiper()?.slideNext();
        };
        const onPrev = (e) => {
            e.preventDefault();
            e.stopPropagation();
            this._getActivePanelSwiper()?.slidePrev();
        };

        nextEl?.addEventListener('click', onNext);
        prevEl?.addEventListener('click', onPrev);
        this._sharedNavBound = true;
    }

    _getActivePanelSwiper() {
        const id = this._activeCollectionId;
        if (!id) return null;
        return this._panels.get(id)?.swiper || null;
    }

    _initSkeletonLayer() {
        if (this._skeletonLayer) return;

        const host = this._panelsRoot || this.querySelector('.tabs-manager-panels') || this.querySelector('.swiper');
        if (!host) return;

        const layer = document.createElement('div');
        layer.className = 'tabs-manager-skeleton-layer';

        const tpl = document.createElement('template');
        tpl.innerHTML = this.renderSkeletons();
        layer.append(...tpl.content.childNodes);

        host.appendChild(layer);
        this._skeletonLayer = layer;
    }

    _showSkeleton() {
        this._initSkeletonLayer();
        this._skeletonLayer?.classList.add('is-visible');
    }

    _hideSkeleton() {
        this._skeletonLayer?.classList.remove('is-visible');
    }

    _isSkeletonMarkup(wrapperOrHtml) {
        if (!wrapperOrHtml) return false;
        if (typeof wrapperOrHtml === 'string') {
            return wrapperOrHtml.includes('class="skeleton') || wrapperOrHtml.includes("class='skeleton");
        }
        return wrapperOrHtml.querySelector('.skeleton') != null;
    }

    _hasValidCache(collectionId) {
        const cached = this.htmlCache[collectionId];
        return Boolean(cached && !this._isSkeletonMarkup(cached));
    }

    _getUniqueCollectionTabs() {
        const seen = new Set();
        const tabs = [];
        // Only tab buttons — panels use data-panel-collection-id
        this.querySelectorAll('button[data-collection-id]').forEach((button) => {
            const id = button.getAttribute('data-collection-id');
            if (!id || seen.has(id)) return;
            seen.add(id);
            tabs.push(button);
        });
        // Fallback for non-button tabs (if any section uses divs)
        if (!tabs.length) {
            this.querySelectorAll('[data-collection-id]').forEach((button) => {
                if (button.hasAttribute('data-panel-collection-id')) return;
                const id = button.getAttribute('data-collection-id');
                if (!id || seen.has(id)) return;
                seen.add(id);
                tabs.push(button);
            });
        }
        return tabs;
    }

    _getActiveCollectionId() {
        return this._activeCollectionId;
    }

    /**
     * Some sections render the same tabs twice (mobile + desktop). Keep both in sync.
     */
    _syncTabMirrors(collectionId) {
        this.querySelectorAll(`button[data-collection-id="${collectionId}"]`).forEach((tab) => {
            tab.classList.add('active');
        });
    }

    /** Toggle which panel is visible via `.is-active` (display none/block). */
    _activatePanel(collectionId) {
        this._panels.forEach((entry, id) => {
            entry.panel.classList.toggle('is-active', id === collectionId);
        });
    }

    /**
     * Put cached HTML into the panel and init Swiper once (first show only).
     * Later visits only update() — avoids destroy/re-init cost on tab switch.
     */
    _hydratePanel(collectionId) {
        this._ensurePanelsStructure();
        const entry = this._panels.get(collectionId);
        if (!entry || !this._hasValidCache(collectionId)) return;

        if (!entry.contentReady) {
            entry.wrapper.innerHTML = this.htmlCache[collectionId];
            entry.contentReady = true;
        }

        if (!entry.swiper && typeof window.Swiper === 'function') {
            entry.swiper = new window.Swiper(entry.swiperEl, this._buildOptionsForPanel(entry));
        } else if (entry.swiper && typeof entry.swiper.update === 'function') {
            entry.swiper.update();
        }
    }

    /**
     * Show a collection panel (no destroy/re-init of other panels).
     */
    _showPanel(collectionId) {
        this._activatePanel(collectionId);
        this._hydratePanel(collectionId);
    }

    // ─── Tab-level CTA helpers ────────────────────────────────────────────────

    /**
     * Returns true when the section is running in tab-level CTA mode.
     * We detect this by checking whether the first tab button carries the
     * data-tab-cta-text attribute (set by Liquid only for cta_type == 'tab').
     */
    get _isTabCtaMode() {
        return this._tabCtaMode;
    }

    initTabCtaRefs() {
        const firstBtn = this.querySelector('[data-collection-id]');
        this._tabCtaMode = firstBtn ? firstBtn.hasAttribute('data-tab-cta-text') : false;
        this._tabDrivenCtaBtn = this.querySelector('.explore-btn--tab-driven');
        this._tabDrivenCtaLabel = this._tabDrivenCtaBtn?.querySelector('.explore-btn__label') || null;
        this._tabDrivenCtaIcon = this.querySelector('.cta-button-icon--tab-driven');
    }

    /**
     * Updates the tab-driven CTA button and icon link to match the given tab
     * button's data attributes.
     * No-ops silently if not in tab CTA mode or button is missing.
     *
     * @param {Element|null} tabButton - The tab <button> that just became active.
     */
    _updateTabCta(tabButton) {
        if (!this._isTabCtaMode || !tabButton) return;

        const ctaBtn = this._tabDrivenCtaBtn;
        if (!ctaBtn) return;

        const text = tabButton.getAttribute('data-tab-cta-text');
        const link = tabButton.getAttribute('data-tab-cta-link') || '#';

        const ctaLabel = this._tabDrivenCtaLabel;
        if (text !== null) {
            if (ctaLabel) {
                if (ctaLabel.textContent !== text) {
                    ctaLabel.textContent = text;
                }
            } else {
                if (ctaBtn.textContent !== text) {
                    ctaBtn.textContent = text;
                }
            }
        }
        if (ctaBtn.getAttribute('href') !== link) {
            ctaBtn.setAttribute('href', link);
        }

        const ctaIcon = this._tabDrivenCtaIcon;
        if (ctaIcon && ctaIcon.getAttribute('href') !== link) {
            ctaIcon.setAttribute('href', link);
        }
    }

    // ─── Observer & tab init ──────────────────────────────────────────────────

    /** Start GraphQL hydrate when the section is ~10% in view (lazy = less work on first paint). */
    initObserver() {
        const start = () => {
            if (this.isFetched) return;
            this.isFetched = true;
            this.observer?.disconnect();
            this.fetchAllCollections();
        };

        if (!('IntersectionObserver' in window)) {
            start();
            return;
        }

        this.observer = new IntersectionObserver(
            (entries) => {
                if (!entries.some((entry) => entry.isIntersecting)) return;
                start();
            },
            {
                root: null,
                rootMargin: '0px',
                threshold: 0.1,
            }
        );

        this.observer.observe(this);
    }

    initTabClicks() {
        this.querySelectorAll('button[data-collection-id]').forEach((button) => {
            button.addEventListener('click', (e) => this.handleTabClick(e));
        });
        // Fallback if tabs aren't buttons
        if (!this.querySelector('button[data-collection-id]')) {
            this.querySelectorAll('[data-collection-id]').forEach((button) => {
                if (button.hasAttribute('data-panel-collection-id')) return;
                button.addEventListener('click', (e) => this.handleTabClick(e));
            });
        }
    }

    // ─── Fetching ─────────────────────────────────────────────────────────────

    /**
     * Load active tab first (show products ASAP), then prefetch other tabs after 2s
     * so later switches are usually cache hits.
     */
    async fetchAllCollections() {
        this._ensurePanelsStructure();

        const tabs = this._getUniqueCollectionTabs();
        if (!tabs.length) return;

        const activeId = this._getActiveCollectionId() || tabs[0].getAttribute('data-collection-id');
        const activeButton = tabs.find((tab) => tab.getAttribute('data-collection-id') === activeId) || tabs[0];

        this._showSkeleton();
        await this.fetchCollection(activeId, activeButton);

        // User may have switched tabs while this fetch was in flight — don't paint/hide for a stale tab.
        if (this._getActiveCollectionId() === activeId) {
            if (this._hasValidCache(activeId)) {
                this._showPanel(activeId);
            }
            this._hideSkeleton();
        }

        const others = tabs.filter((tab) => tab.getAttribute('data-collection-id') !== activeId);
        const prefetch = () => {
            others.forEach(async (tab) => {
                const id = tab.getAttribute('data-collection-id');
                if (!id) return;
                await this.fetchCollection(id, tab);
                // Warm HTML into the panel wrapper (Swiper still inits on first show).
                const entry = this._panels.get(id);
                if (entry && !entry.contentReady && this._hasValidCache(id)) {
                    entry.wrapper.innerHTML = this.htmlCache[id];
                    entry.contentReady = true;
                }
            });
        };

        setTimeout(prefetch, 2000);
    }

    /** Fetch one collection; reuse in-flight promise / cache when possible. */
    fetchCollection(collectionId, button) {
        if (!collectionId) return Promise.resolve(null);
        if (this._hasValidCache(collectionId)) return Promise.resolve(this.htmlCache[collectionId]);
        if (this._inflightFetches.has(collectionId)) return this._inflightFetches.get(collectionId);

        const promise = this._fetchCollectionProducts(collectionId, button).finally(() => {
            this._inflightFetches.delete(collectionId);
        });
        this._inflightFetches.set(collectionId, promise);
        return promise;
    }

    async _fetchCollectionProducts(collectionId, button) {
        const gid = collectionId.includes('gid://') ? collectionId : `gid://shopify/Collection/${collectionId}`;

        // Fetches metafields needed by all card types using GraphQL field aliases.
        // Limit is 15 products per collection.
        const query = `
            query getCollectionProducts($id: ID!) {
            collection(id: $id) {
                products(first: 15) {
                nodes {
                    id
                    title
                    handle
                    tags

                    variants(first: 1) {
                    nodes {
                        id
                    }
                    }

                    featuredImage {
                    url(transform: { maxWidth: 800 })
                    }

                    priceRange {
                    minVariantPrice {
                        amount
                        currencyCode
                    }
                    }

                    compareAtPriceRange {
                    minVariantPrice {
                        amount
                    }
                    }

                    neo_usp: metafield(namespace: "custom", key: "neo_usp") {
                    value
                    }

                    highlights: metafield(namespace: "neo", key: "highlights") {
                    value
                    }

                    tag: metafield(namespace: "neo", key: "usage_tag") {
                    value
                    }

                    key_highlights: metafield(namespace: "custom", key: "key_highlights") {
                    value
                    }

                    product_specs: metafield(namespace: "neo", key: "neo_product_card_3_product_specs") {
                    value
                    }
                }
                }
            }
            }
        `;

        try {
            const response = await fetch(`https://${this.SHOPIFY_DOMAIN}/api/${this.SHOPIFY_API_VERSION}/graphql.json`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Shopify-Storefront-Access-Token': this.SHOPIFY_STOREFRONT_ACCESS_TOKEN,
                },
                body: JSON.stringify({
                    query,
                    variables: { id: gid }
                }),
            });

            const json = await response.json();
            if (json.data && json.data.collection) {
                const products = json.data.collection.products.nodes;
                this.querySelectorAll(`[data-collection-id="${collectionId}"]`).forEach((tab) => {
                    tab.setAttribute('data-collection', JSON.stringify(products));
                });
                // Cache GraphQL shell HTML only — never serialize hydrated custom elements
                this.htmlCache[collectionId] = products.map((p) => this.renderProductCard(p)).join('');
                return this.htmlCache[collectionId];
            }
        } catch (error) {
            console.error('TabsManager: Error fetching collection', collectionId, error);
        }
        return null;
    }

    // ─── Tab click handler ────────────────────────────────────────────────────

    /**
     * INP-sensitive path:
     *  - Sync: mark tab active (+ optional skeleton) so the UI responds immediately
     *  - setTimeout(0): panel swap, fetch if needed, Swiper hydrate/update
     * Revisit (Swiper already exists) = show/hide only — no rebuild.
     */
    handleTabClick(event) {
        const button = event.currentTarget;
        const collectionId = button.getAttribute('data-collection-id');
        if (!collectionId) return;
        if (button.classList.contains('active')) return;

        this._ensurePanelsStructure();

        const prevCollectionId = this._activeCollectionId;
        this._activeCollectionId = collectionId;

        // Sync: cheapest visual feedback first (active tab).
        if (prevCollectionId && prevCollectionId !== collectionId) {
            this.querySelectorAll(`button[data-collection-id="${prevCollectionId}"]`).forEach((tab) => {
                tab.classList.remove('active');
            });
        }
        button.classList.add('active');
        this._showSkeleton();
        
        // Deferred: avoid heavy DOM/Swiper work on the click's critical path.
        setTimeout(async () => {
            this._updateTabCta(button);
            if (this._activeCollectionId !== collectionId) return;

            this._syncTabMirrors(collectionId);

            const entry = this._panels.get(collectionId);

            // Already hydrated: just show this panel.
            if (entry?.swiper) {
                this._activatePanel(collectionId);
                entry.swiper?.update?.();
                this._hideSkeleton();
                return;
            }

            this._activatePanel(collectionId);

            if (!this._hasValidCache(collectionId)) {
                await this.fetchCollection(collectionId, button);
            }

            if (this._activeCollectionId !== collectionId) return;

            this._showPanel(collectionId);
            this._hideSkeleton();
        }, 0);
    }

    // ─── Legacy name kept for safety; delegates to per-panel show ─────────────

    updateSlider(collectionId) {
        this._showPanel(collectionId);
    }

    // ─── Skeleton Dispatcher ──────────────────────────────────────────────────

    renderSkeletons() {
        switch (this.productCard) {
            case '1': return this.renderSkeletons1();
            case '2': return this.renderSkeletons2();
            case '3': return this.renderSkeletons3();
            default: return this.renderSkeletons3();
        }
    }

    renderSkeletons1() {
        let skeletons = '';
        for (let i = 0; i < 4; i++) {
            skeletons += `
                <div class="swiper-slide">
                    <neo-product-card-1 class="neo-product-card-1">
                        <div class="neo-product-card-details">
                            <div class="title-container">
                                <div class="skeleton skeleton-text" style="width: 75%; height: 16px; margin-bottom: 6px;"></div>
                                <div class="skeleton skeleton-text" style="width: 50%; height: 12px;"></div>
                            </div>
                            <div class="neo-product-card-wishlist">
                                <div class="skeleton" style="width: 24px; height: 24px; border-radius: 50%;"></div>
                            </div>
                        </div>
                        <div class="neo-product-card-media-container">
                            <div class="color-variant-images">
                                <span class="neo-product-card-color-skeleton" aria-hidden="true"></span>
                                <span class="neo-product-card-color-skeleton" aria-hidden="true"></span>
                                <span class="neo-product-card-color-skeleton" aria-hidden="true"></span>
                                <span class="neo-product-card-color-skeleton" aria-hidden="true"></span>
                            </div>
                            <div class="neo-product-card-media">
                                <div class="skeleton skeleton-image" style="aspect-ratio: 1; width: 100%;"></div>
                            </div>
                            <div class="skeleton" style="height: 44px; width: 100%; border-radius: 4px; margin-top: 12px;"></div>
                        </div>
                    </neo-product-card-1>
                </div>
            `;
        }
        return skeletons;
    }

    renderSkeletons2() {
        let skeletons = '';
        for (let i = 0; i < 4; i++) {
            skeletons += `
                <div class="swiper-slide">
                    <div class="neo-product-card-2 relative p-0">
                        <div class="neo-product-card-2__link">
                            <div class="skeleton skeleton-image" style="width: 130px; height: 130px;"></div>
                            <div class="neo-product-card-2__product-details flex flex-col gap-4">
                                <div class="skeleton skeleton-text" style="width: 80%; height: 18px;"></div>
                                <div class="skeleton skeleton-text" style="width: 60%; height: 13px;"></div>
                                <div class="skeleton skeleton-text" style="width: 40%; height: 16px;"></div>
                            </div>
                        </div>
                        <div class="skeleton" style="height: 44px; width: 100%; border-radius: 4px; margin-top: 12px;"></div>
                    </div>
                </div>
            `;
        }
        return skeletons;
    }

    renderSkeletons3() {
        let skeletons = '';
        for (let i = 0; i < 4; i++) {
            skeletons += `
                <div class="swiper-slide">
                    <div class="neo-product-card-3 relative">
                        <div class="neo-product-card-3__link">
                            <div class="skeleton skeleton-text" style="width: 65%; height: 20px; margin: 0 auto 10px;"></div>
                            <div class="flex justify-center items-center gap-2" style="margin-bottom: 10px;">
                                <div class="skeleton skeleton-text" style="width: 50px; height: 22px; border-radius: 4px;"></div>
                                <div class="skeleton skeleton-text" style="width: 60px; height: 22px; border-radius: 4px;"></div>
                            </div>
                            <div class="skeleton skeleton-image" style="aspect-ratio: 1; width: 100%;"></div>
                        </div>
                        <div class="skeleton" style="height: 44px; width: 100%; border-radius: 4px; margin-top: 12px;"></div>
                    </div>
                </div>
            `;
        }
        return skeletons;
    }

    // ─── Card renderers ───────────────────────────────────────────────────────

    renderProductCard(product) {
        switch (this.productCard) {
            case '1': return this.renderProductCard1(product);
            case '2': return this.renderProductCard2(product);
            case '3': return this.renderProductCard3(product);
            default: return this.renderProductCard3(product);
        }
    }

    renderProductCard1(product) {
        const highlights = product.highlights?.value
            ? JSON.parse(product.highlights.value)
            : [];
        const highlightsText = highlights.join(' | ');
        const tag = product.tag?.value || '';
        const productUrl = `/products/${product.handle}`;
        const productId = product.id.replace('gid://shopify/Product/', '');
        const variantId = product.variants?.nodes?.[0]?.id?.split('/').pop();
        const hasNewTag = (product.tags || []).join(' ').toLowerCase().includes('new 2026');
        const hasSaleTag = (product.tags || []).join(' ').toLowerCase().includes('sale 2026');

        return ` <div class="swiper-slide">
            <neo-product-card-1 class="neo-product-card-1" data-product-id="${productId}" data-enable-swiper="false" data-enable-pagination="false">
            <div class="neo-product-card-details">
            <a class="title-container" href="${productUrl}">
            <h2 class="visually-hidden">${product.title}</h2>

            ${product.title ? `
            <p class="neo-product-card-title line-clamp-2 min-height">
            ${product.title}
            </p>
            ` : ``}

            ${highlightsText ? `
            <p class="neo-product-card-sub-title line-clamp-2 min-height">
            ${highlightsText}
            </p>
            ` : ``}




            </a>

            ${productId && variantId ? `
            <div class="neo-product-card-wishlist">
            <span
            class="smartwishlist"
            data-product="${productId}"
            data-variant="${variantId}"
            ></span>
            </div>
            ` : ``}

            </div>

            <div class="color-variant-images">
                <span class="neo-product-card-color-skeleton" aria-hidden="true"></span>
                <span class="neo-product-card-color-skeleton" aria-hidden="true"></span>
                <span class="neo-product-card-color-skeleton" aria-hidden="true"></span>
                <span class="neo-product-card-color-skeleton" aria-hidden="true"></span>
            </div>

            ${product.featuredImage?.url || product.tag || product.variantId ? `
            <div class="neo-product-card-media-container">

            ${hasNewTag ? `<p class="neo-product-card-new-tag">New</p>` : ``}
            ${hasSaleTag ? `<p class="neo-product-card-sale-tag">Sale</p>` : ``}

            <div class="neo-product-card-media">

            ${product.featuredImage?.url ? `
            <div class="swiper">
            <div class="swiper-wrapper">

            <div class="swiper-slide">

            <a class="neo-product-card-media--link" href="${productUrl}">
            <img
            src="${product.featuredImage.url}?width=700"
            srcset="${product.featuredImage.url}?width=220 220w, ${product.featuredImage.url}?width=360 360w, ${product.featuredImage.url}?width=700 700w"
            alt="${product.featuredImage.altText || product.title}"
            width="520"
            height="578"
            class="neo-product-card-1__image-img"
            />
            </a>

            </div>

            </div>
            </div>
            ` : ``}

            ${tag ? `
            <p class="neo-product-card-1-tag">
            ${tag}
            </p>
            ` : ``}

            </div>

            ${variantId ? `
            <button class="add-to-cart-button-gql  cursor-pointer" data-product-id="${productId}" data-variant-id="${variantId}" onclick="ATBModalOpen(event, '${productId}')">

            <span class="atc-link-button__corner atc-link-button__corner--tl">
            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
                <mask id="corner-mask-tl" fill="white">
                <path d="M0 2C0 0.895431 0.895431 0 2 0H6C7.10457 0 8 0.895431 8 2V6C8 7.10457 7.10457 8 6 8H2C0.895431 8 0 7.10457 0 6V2Z" />
                </mask>
                <path d="M-0.5 2C-0.5 0.619288 0.619288 -0.5 2 -0.5H5.5C6.88071 -0.5 8 0.619288 8 2C8 1.17157 7.10457 0.5 6 0.5H2C1.17157 0.5 0.5 1.17157 0.5 2H-0.5ZM8 8H0H8ZM2 8C0.619288 8 -0.5 6.88071 -0.5 5.5V2C-0.5 0.619288 0.619288 -0.5 2 -0.5V0.5C1.17157 0.5 0.5 1.17157 0.5 2V6C0.5 7.10457 1.17157 8 2 8ZM8 0V8V0Z" fill="#ffffff" mask="url(#corner-mask-tl)" />
            </svg>
            </span>

            <span class="atc-link-button__corner atc-link-button__corner--tr">
            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
                <mask id="corner-mask-tl" fill="white">
                <path d="M0 2C0 0.895431 0.895431 0 2 0H6C7.10457 0 8 0.895431 8 2V6C8 7.10457 7.10457 8 6 8H2C0.895431 0 0 7.10457 0 6V2Z" />
                </mask>
                <path d="M-0.5 2C-0.5 0.619288 0.619288 -0.5 2 -0.5H5.5C6.88071 -0.5 8 0.619288 8 2C8 1.17157 7.10457 0.5 6 0.5H2C1.17157 0.5 0.5 1.17157 0.5 2H-0.5ZM8 8H0H8ZM2 8C0.619288 8 -0.5 6.88071 -0.5 5.5V2C-0.5 0.619288 0.619288 -0.5 2 -0.5V0.5C1.17157 0.5 0.5 1.17157 0.5 2V6C0.5 7.10457 1.17157 8 2 8ZM8 0V8V0Z" fill="#ffffff" mask="url(#corner-mask-tl)" />
            </svg>
            </span>
            <span class="atc-link-button__corner atc-link-button__corner--bl">
            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
                <mask id="corner-mask-tl" fill="white">
                <path d="M0 2C0 0.895431 0.895431 0 2 0H6C7.10457 0 8 0.895431 8 2V6C8 7.10457 7.10457 8 6 8H2C0.895431 8 0 7.10457 0 6V2Z" />
                </mask>
                <path d="M-0.5 2C-0.5 0.619288 0.619288 -0.5 2 -0.5H5.5C6.88071 -0.5 8 0.619288 8 2C8 1.17157 7.10457 0.5 6 0.5H2C1.17157 0.5 0.5 1.17157 0.5 2H-0.5ZM8 8H0H8ZM2 8C0.619288 8 -0.5 6.88071 -0.5 5.5V2C-0.5 0.619288 0.619288 -0.5 2 -0.5V0.5C1.17157 0.5 0.5 1.17157 0.5 2V6C0.5 7.10457 1.17157 8 2 8ZM8 0V8V0Z" fill="#ffffff" mask="url(#corner-mask-tl)" />
            </svg>
            </span>

            <span class="atc-link-button__corner atc-link-button__corner--br">
            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
                <mask id="corner-mask-tl" fill="white">
                <path d="M0 2C0 0.895431 0.895431 0 2 0H6C7.10457 0 8 0.895431 8 2V6C8 7.10457 7.10457 8 6 8H2C0.895431 0 0 7.10457 0 6V2Z" />
                </mask>
                <path d="M-0.5 2C-0.5 0.619288 0.619288 -0.5 2 -0.5H5.5C6.88071 -0.5 8 0.619288 8 2C8 1.17157 7.10457 0.5 6 0.5H2C1.17157 0.5 0.5 1.17157 0.5 2H-0.5ZM8 8H0H8ZM2 8C0.619288 8 -0.5 6.88071 -0.5 5.5V2C-0.5 0.619288 0.619288 -0.5 2 -0.5V0.5C1.17157 0.5 0.5 1.17157 0.5 2V6C0.5 7.10457 1.17157 8 2 8ZM8 0V8V0Z" fill="#ffffff" mask="url(#corner-mask-tl)" />
            </svg>
            </span>

            ${this.formatAtcPriceHtml(product)}
            <span>|</span>
            ${window.AtcPriceBlock && window.AtcPriceBlock.ctaPlusHtml ? window.AtcPriceBlock.ctaPlusHtml() : '<span class="add-to-cart-button-gql-plus"><span class="add-to-cart-button-gql-plus-icon">+</span><span class="add-to-cart-button-gql-label">Add</span></span>'}
            </button>
            ` : ``}

            </div>
            ` : ``}

            </neo-product-card-1>
            </div>
        `
    }

    // ─── Card Type 2 — neo-product-card-2 style ───────────────────────────────

    /**
     * Renders a neo-product-card-2 style card.
     * Uses: title, key_highlights (custom.key_highlights), featuredImage, price
     */
    renderProductCard2(product) {
        const highlights = product.key_highlights?.value ? JSON.parse(product.key_highlights.value) : [];
        const highlightText = highlights.length > 0 ? highlights[0].substring(0, 100) : '';
        const productUrl = `/products/${product.handle}`;
        const productId = product.id.replace('gid://shopify/Product/', '');

        return `
            <div class="swiper-slide">
                <div class="neo-product-card-2 relative p-0">
                    <a class="neo-product-card-2__link" href="${productUrl}">
                        <img
                            src="${product.featuredImage?.url || ''}"
                            class="neo-product-card-2__image"
                            alt="${product.title}"
                        >
                        <div class="neo-product-card-2__product-details flex flex-col gap-4">
                            <h3 class="neo-product-card-2__title athletics-semibold m-0">
                                ${product.title}
                            </h3>
                            ${highlightText
                ? `<p class="neo-product-card-2__product-description athletics-medium m-0">${highlightText}</p>`
                : ''}
                            <span class="neo-product-card-2__price athletics-semibold m-0">
                                ${this.formatPrice(product)}
                            </span>
                        </div>
                    </a>
                    <button
                        onclick="ATBModalOpen(event, '${productId}')"
                        class="neo-product-card-2__ATC_button"
                    >
                        <span class="athletics-semibold text-white product-item-card__button-text m-0">
                            + Add to Cart
                        </span>
                        ${this.cornerSvgHtml()}
                    </button>
                </div>
            </div>
        `;
    }

    // ─── Card Type 3 — neo-product-card-3 style (default) ────────────────────

    /**
     * Renders a neo-product-card-3 style card.
     * Uses: title, product_specs (neo.neo_product_card_3_product_specs), featuredImage, price
     */
    renderProductCard3(product) {
        const specs = product.product_specs?.value ? JSON.parse(product.product_specs.value) : [];
        const specsHtml = specs.map(spec => `<div class="neo-product-card-3__tag"><span>${spec}</span></div>`).join('');
        const productUrl = `/products/${product.handle}`;
        const productId = product.id.replace('gid://shopify/Product/', '');
        return `
            <div class="swiper-slide">
                <div class="neo-product-card-3 relative">
                    <a class="neo-product-card-3__link" href="${productUrl}">
                        <h3 class="font-athletics-semibold text-white text-center text-base line-clamp-2" style="--text-size:20px">
                            ${product.title}
                        </h3>
                        ${specs.length > 0
                ? `<div class="neo-product-card-3__tags-container flex justify-center items-center relative">
                                    ${specsHtml}
                               </div>`
                : ''}
                        <img
                            src="${product.featuredImage?.url}"
                            alt="${product.title}"
                            srcset="${product.featuredImage?.url}?width=352 352w, ${product.featuredImage?.url}?width=600 600w"
                            width="600"
                            height="667"
                            class="neo-product-card-3__image"
                        >
                    </a>
                    <button
                        onclick="ATBModalOpen(event, '${productId}')"
                        class="neo-product-card-3__ATC_button relative"
                    >
                        <p class="athletics-semibold text-white font-semibold product-item-card__price m-0">
                            ${this.formatAtcPriceHtml(product)}
                        </p>
                        <span class="neo-product-card-3__price-divider">|</span>
                        <p class="athletics-semibold text-white font-semibold product-item-card__button-text m-0">
                              <svg
                                class="loading-svg"
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="#fff"
                                xmlns="http://www.w3.org/2000/svg"
                                >
                                <style>.spinner_aj0A{transform-origin:center;animation:spinner_KYSC .75s infinite linear}@keyframes spinner_KYSC{100%{transform:rotate(360deg)}}</style><path d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z" class="spinner_aj0A"></path>
                                </svg>
                            <span class="neo-product-card-3__plus-icon">+</span>
                            Add
                            ${(window.AtcPriceBlock && window.AtcPriceBlock.bagHtml) || ''}
                        </p>
                        ${this.cornerSvgHtml()}
                    </button>
                </div>
            </div>
        `;
    }

    // ─── Helpers ──────────────────────────────────────────────────────────────

    /**
     * Formats the product's minimum variant price using Intl.NumberFormat.
     * @param {Object} product - Product node from Storefront API
     * @returns {string} Formatted price string (e.g. "$29.99")
     */
    formatPrice(product) {
        const amount = product?.priceRange?.minVariantPrice?.amount || 0;

        const formatted = new Intl.NumberFormat('en-IN', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(amount);

        return `₹ ${formatted}`;
    }

    formatAtcPriceHtml(product) {
        const amount = parseFloat(product?.priceRange?.minVariantPrice?.amount || 0);
        const compare = parseFloat(product?.compareAtPriceRange?.minVariantPrice?.amount || 0);
        if (window.AtcPriceBlock && typeof window.AtcPriceBlock.html === 'function') {
            return window.AtcPriceBlock.html(amount, compare);
        }
        return `<span class="atc-price-block"><span class="atc-price-block__sale">${this.formatPrice(product)}</span></span>`;
    }

    /**
     * Returns the four corner SVG decorations used on ATC buttons.
     * @returns {string} HTML string with four cornor divs
     */
    cornerSvgHtml() {
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none"><mask id="path-1-inside-1_6289_29312" fill="white"><path d="M0 2C0 0.895431 0.895431 0 2 0H6C7.10457 0 8 0.895431 8 2V6C8 7.10457 7.10457 8 6 8H2C0.895431 8 0 7.10457 0 6V2Z"/></mask><path d="M-0.5 2C-0.5 0.619288 0.619288 -0.5 2 -0.5H5.5C6.88071 -0.5 8 0.619288 8 2C8 1.17157 7.10457 0.5 6 0.5H2C1.17157 0.5 0.5 1.17157 0.5 2V6C0.5 7.10457 1.17157 8 2 8ZM8 0V8V0Z" fill="white" mask="url(#path-1-inside-1_6289_29312)"/></svg>`;
        return `
            <div class="cornor-1">${svg}</div>
            <div class="cornor-2" style="transform: rotate(90deg);">${svg}</div>
            <div class="cornor-3" style="transform: rotate(265deg);">${svg}</div>
            <div class="cornor-4" style="transform: rotate(180deg);">${svg}</div>
        `;
    }
}

if (!customElements.get('tabs-manager')) {
    customElements.define('tabs-manager', TabsManager);
}
